import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/form/Input';
import { Select } from '../../components/form/Select';
import { Checkbox } from '../../components/form/Checkbox';
import { FileUploader } from '../../components/ui/FileUploader/FileUploader';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { PageLoader } from '../../components/ui/PageLoader/PageLoader';
import { useToast } from '../../hooks/useToast';
import { useReceiptDetail } from './hooks/useReceiptDetail';
import { useReceiptPhotos } from './hooks/useReceiptPhotos';
import { BankDetailsUpdateModal } from './components/BankDetailsUpdateModal';
import {
  useCheckBankDetails,
  useDeleteReceiptPhoto,
  useUpdateReceipt,
  useUploadReceiptPhotos,
} from './hooks/useReceiptMutations';
import { usePaymentModes } from '../bookings/hooks/useReferenceData';
import type { ReceiptPhotoDto, UpdateReceiptInput } from '../../types/api';
import styles from './ReceiptDirectCreatePage.module.css';

const MAX_RECEIPT_PHOTOS = 7;

interface FormState {
  car_registration: string;
  car_make_model: string;
  car_color: string;
  car_year: string;
  car_mileage: string;
  chassis: string;
  has_mot: boolean;
  has_keys: boolean;
  has_logbook: boolean;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_address: string;
  customer_postcode: string;
  amount: string;
  payment_mode_uuid: string;
  cheque_number: string;
  account_name: string;
  account_number: string;
  sort_code: string;
  notes: string;
}

export function ReceiptEditPage(): JSX.Element {
  const { enc_id } = useParams<{ enc_id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const detail = useReceiptDetail(enc_id ?? '');
  const updateMutation = useUpdateReceipt(enc_id ?? '');
  const checkBank = useCheckBankDetails();
  const [bankPrompt, setBankPrompt] = useState<{
    input: UpdateReceiptInput;
    fields: string[];
  } | null>(null);
  const paymentModes = usePaymentModes();
  const photos = useReceiptPhotos(enc_id ?? '', { enabled: Boolean(enc_id) });
  const uploadPhotos = useUploadReceiptPhotos();
  const deletePhoto = useDeleteReceiptPhoto(enc_id ?? '');

  const [form, setForm] = useState<FormState | null>(null);
  const [newPhotos, setNewPhotos] = useState<File[]>([]);
  const [photoToDelete, setPhotoToDelete] = useState<ReceiptPhotoDto | null>(null);

  const existingPhotos = photos.data ?? [];
  const remainingSlots = Math.max(
    0,
    MAX_RECEIPT_PHOTOS - existingPhotos.filter((p) => p.source !== 'customer').length,
  );

  async function handleUploadNewPhotos() {
    if (!enc_id || newPhotos.length === 0) return;
    try {
      await uploadPhotos.mutateAsync({ encId: enc_id, files: newPhotos });
      setNewPhotos([]);
      toast.success(newPhotos.length === 1 ? 'Photo uploaded' : 'Photos uploaded');
    } catch {
      // error toast handled in the mutation
    }
  }

  function confirmDeletePhoto() {
    if (!photoToDelete) return;
    deletePhoto.mutate(photoToDelete.enc_id, {
      onSuccess: () => {
        toast.success('Photo deleted');
        setPhotoToDelete(null);
      },
    });
  }

  // Resolve the saved payment_mode_name to a uuid once both the receipt
  // and the payment_modes reference list are available. The receipt
  // detail endpoint exposes `payment_mode_name` (joined from
  // payment_modes.name in receipts.queries.ts) but no uuid, so we look
  // up the matching reference-data row by name.
  useEffect(() => {
    if (!detail.data || form !== null) return;
    if (paymentModes.data === undefined) return;

    const r = detail.data;
    const matchedMode = (paymentModes.data ?? []).find(
      (p) => p.name === r.payment_mode_name,
    );

    setForm({
      car_registration: r.car_registration,
      car_make_model: r.car_make_model,
      car_color: r.car_color ?? '',
      car_year: r.car_year !== null ? String(r.car_year) : '',
      car_mileage: r.car_mileage !== null ? String(r.car_mileage) : '',
      chassis: r.chassis ?? '',
      has_mot: r.has_mot,
      has_keys: r.has_keys,
      has_logbook: r.has_logbook,
      customer_name: r.customer_name,
      customer_email: r.customer_email ?? '',
      customer_phone: r.customer_phone,
      customer_address: r.customer_address,
      customer_postcode: r.customer_postcode,
      amount: String(r.amount),
      payment_mode_uuid: matchedMode?.uuid ?? '',
      cheque_number: r.cheque_number ?? '',
      account_name: r.account_name ?? '',
      account_number: r.account_number ?? '',
      sort_code: r.sort_code ?? '',
      notes: r.notes ?? '',
    });
  }, [detail.data, form, paymentModes.data]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  const paymentOptions = useMemo(
    () =>
      (paymentModes.data ?? []).map((p) => ({
        value: p.uuid,
        label: p.name,
      })),
    [paymentModes.data],
  );

  const errors = useMemo(() => {
    if (!form) return [];
    const e: string[] = [];
    if (!form.car_registration.trim()) e.push('Vehicle registration is required.');
    if (!form.customer_name.trim()) e.push('Customer name is required.');
    if (!form.amount || Number.isNaN(Number(form.amount))) e.push('Amount is required.');
    return e;
  }, [form]);

  const canSubmit = !!form && errors.length === 0 && !updateMutation.isPending;

  async function handleSubmit() {
    if (!canSubmit || !form) return;
    const input: UpdateReceiptInput = {
      car_registration: form.car_registration.trim().toUpperCase(),
      car_make_model: form.car_make_model.trim(),
      car_color: form.car_color.trim() || undefined,
      car_year: form.car_year ? Number(form.car_year) : undefined,
      car_mileage: form.car_mileage ? Number(form.car_mileage) : undefined,
      chassis: form.chassis.trim() || undefined,
      has_mot: form.has_mot,
      has_keys: form.has_keys,
      has_logbook: form.has_logbook,
      customer_name: form.customer_name.trim(),
      customer_email: form.customer_email.trim() || null,
      customer_phone: form.customer_phone.trim(),
      customer_address: form.customer_address.trim(),
      customer_postcode: form.customer_postcode.trim().toUpperCase(),
      amount: Number(form.amount),
      payment_mode_uuid: form.payment_mode_uuid || undefined,
      cheque_number: form.cheque_number.trim() || null,
      account_name: form.account_name.trim() || null,
      account_number: form.account_number.trim() || null,
      sort_code: form.sort_code.trim() || null,
      notes: form.notes.trim() || null,
    };
    // Batch 7b: ask before overwriting the customer's stored bank details.
    const hasBank =
      input.account_name != null || input.account_number != null || input.sort_code != null;
    if (hasBank && enc_id) {
      try {
        const check = await checkBank.mutateAsync({
          receipt_enc_id: enc_id,
          account_name: input.account_name ?? null,
          account_number: input.account_number ?? null,
          sort_code: input.sort_code ?? null,
        });
        if (check.differs) {
          setBankPrompt({ input, fields: check.fields });
          return;
        }
      } catch {
        // Check failed: save without overwriting (first capture still applies).
      }
    }
    await saveReceipt(input);
  }

  async function saveReceipt(input: UpdateReceiptInput) {
    try {
      await updateMutation.mutateAsync(input);
      toast.success('Receipt updated');
      navigate(`/collections/receipts/${encodeURIComponent(enc_id ?? '')}`);
    } catch {
      // toast handled
    }
  }

  if (!enc_id) return <PageLoader message="Loading…" />;
  if (detail.isLoading || !form) return <PageLoader message="Loading receipt…" />;
  if (detail.isError || !detail.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Receipt not found" />
        <Card>
          <p>Could not load receipt.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={`Edit Receipt ${detail.data.unique_id}`}
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate(`/collections/receipts/${encodeURIComponent(enc_id)}`)}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
        }
      />

      <Card title="Vehicle">
        <div className={styles.grid2}>
          <Input
            label="Registration"
            required
            value={form.car_registration}
            onChange={(e) => setField('car_registration', e.target.value.toUpperCase())}
          />
          <Input
            label="Make / Model"
            value={form.car_make_model}
            onChange={(e) => setField('car_make_model', e.target.value)}
          />
          <Input
            label="Colour"
            value={form.car_color}
            onChange={(e) => setField('car_color', e.target.value)}
          />
          <Input
            label="Year"
            variant="number"
            value={form.car_year}
            onChange={(e) => setField('car_year', e.target.value)}
          />
          <Input
            label="Mileage"
            variant="number"
            value={form.car_mileage}
            onChange={(e) => setField('car_mileage', e.target.value)}
          />
          <Input
            label="Chassis"
            value={form.chassis}
            onChange={(e) => setField('chassis', e.target.value)}
          />
        </div>
        <div className={styles.checkboxRow} style={{ marginTop: '0.75rem' }}>
          <Checkbox
            label="MOT present"
            checked={form.has_mot}
            onChange={(e) => setField('has_mot', e.target.checked)}
          />
          <Checkbox
            label="Keys present"
            checked={form.has_keys}
            onChange={(e) => setField('has_keys', e.target.checked)}
          />
          <Checkbox
            label="Logbook present"
            checked={form.has_logbook}
            onChange={(e) => setField('has_logbook', e.target.checked)}
          />
        </div>
      </Card>

      <Card title="Customer">
        <div className={styles.grid2}>
          <Input
            label="Name"
            required
            value={form.customer_name}
            onChange={(e) => setField('customer_name', e.target.value)}
          />
          <Input
            label="Phone"
            variant="tel"
            value={form.customer_phone}
            onChange={(e) => setField('customer_phone', e.target.value)}
          />
          <Input
            label="Email"
            variant="email"
            value={form.customer_email}
            onChange={(e) => setField('customer_email', e.target.value)}
          />
          <Input
            label="Amount (£)"
            variant="number"
            required
            value={form.amount}
            onChange={(e) => setField('amount', e.target.value)}
          />
          <Input
            label="Address"
            value={form.customer_address}
            onChange={(e) => setField('customer_address', e.target.value)}
          />
          <Input
            label="Postcode"
            value={form.customer_postcode}
            onChange={(e) => setField('customer_postcode', e.target.value.toUpperCase())}
          />
        </div>
      </Card>

      <Card title="Payment">
        <div className={styles.grid2}>
          <Select
            label="Payment method"
            options={paymentOptions}
            placeholder={
              paymentModes.isLoading
                ? 'Loading…'
                : paymentOptions.length === 0
                  ? 'No options — leave blank'
                  : '—'
            }
            value={form.payment_mode_uuid}
            onChange={(e) => setField('payment_mode_uuid', e.target.value)}
          />
          <Input
            label="Cheque Number"
            value={form.cheque_number}
            onChange={(e) => setField('cheque_number', e.target.value)}
          />
          <Input
            label="Account Name"
            value={form.account_name}
            onChange={(e) => setField('account_name', e.target.value)}
          />
          <Input
            label="Account Number"
            value={form.account_number}
            onChange={(e) => setField('account_number', e.target.value)}
          />
          <Input
            label="Sort Code"
            value={form.sort_code}
            onChange={(e) => setField('sort_code', e.target.value)}
          />
        </div>
      </Card>

      <Card title="Photos">
        {photos.isLoading ? (
          <p>Loading photos…</p>
        ) : existingPhotos.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>No photos attached.</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {existingPhotos.map((photo) => (
              <div key={photo.enc_id} style={{ position: 'relative', width: 120 }}>
                {photo.presigned_url ? (
                  <img
                    src={photo.presigned_url}
                    alt={photo.caption ?? 'Receipt photo'}
                    style={{
                      width: 120,
                      height: 120,
                      objectFit: 'cover',
                      borderRadius: 6,
                      border: '1px solid var(--color-border)',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 120,
                      height: 120,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 6,
                      border: '1px solid var(--color-border)',
                      fontSize: 12,
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    Preview unavailable
                  </div>
                )}
                {photo.source === 'customer' ? (
                  <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                    Stored on customer record
                  </span>
                ) : (
                <Button
                  variant="icon"
                  icon={Trash2}
                  aria-label="Delete photo"
                  onClick={() => setPhotoToDelete(photo)}
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    background: 'var(--color-danger)',
                    color: '#fff',
                  }}
                />
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          {remainingSlots > 0 ? (
            <>
              <FileUploader value={newPhotos} onChange={setNewPhotos} maxFiles={remainingSlots} />
              {newPhotos.length > 0 ? (
                <Button
                  variant="secondary"
                  onClick={() => void handleUploadNewPhotos()}
                  loading={uploadPhotos.isPending}
                  style={{ marginTop: 8 }}
                >
                  {newPhotos.length === 1
                    ? 'Upload 1 photo'
                    : `Upload ${newPhotos.length} photos`}
                </Button>
              ) : null}
            </>
          ) : (
            <p style={{ color: 'var(--color-text-muted)' }}>
              Maximum of {MAX_RECEIPT_PHOTOS} photos reached.
            </p>
          )}
        </div>
      </Card>

      <Card title="Notes">
        <textarea
          rows={3}
          style={{ width: '100%', resize: 'vertical' }}
          value={form.notes}
          onChange={(e) => setField('notes', e.target.value)}
          aria-label="Notes"
        />
      </Card>

      <div className={styles.submitBar}>
        <ul className={styles.errorList} aria-live="polite">
          {errors.slice(0, 4).map((msg) => (
            <li key={msg}>{msg}</li>
          ))}
        </ul>
        <Button
          variant="primary"
          onClick={() => void handleSubmit()}
          loading={updateMutation.isPending}
          disabled={!canSubmit}
        >
          Save changes
        </Button>
      </div>

      <BankDetailsUpdateModal
        open={bankPrompt !== null}
        fields={bankPrompt?.fields ?? []}
        loading={updateMutation.isPending}
        onYes={() => {
          const p = bankPrompt;
          setBankPrompt(null);
          if (p !== null) void saveReceipt({ ...p.input, update_customer_bank_details: true });
        }}
        onNo={() => {
          const p = bankPrompt;
          setBankPrompt(null);
          if (p !== null) void saveReceipt({ ...p.input, update_customer_bank_details: false });
        }}
        onBack={() => setBankPrompt(null)}
      />

      <ConfirmModal
        open={photoToDelete !== null}
        title="Delete this photo?"
        message="This permanently removes the photo from the receipt and cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deletePhoto.isPending}
        onCancel={() => setPhotoToDelete(null)}
        onConfirm={confirmDeletePhoto}
      />
    </div>
  );
}
